import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ShieldCheck } from "lucide-react";

const STATUS_CLS = {
  "Live validated": "text-emerald-300 border-emerald-400/40 bg-emerald-400/10",
  "Pending live validation": "text-amber-300 border-amber-400/30 bg-amber-500/[0.05]",
  Error: "text-rose-300 border-rose-400/40 bg-rose-500/10",
};
const cls = (s) => {
  const t = String(s || "");
  if (t.startsWith("Live — verified") || t.startsWith("Live — collecting") || t === "Live validated")
    return "text-emerald-300 border-emerald-400/40 bg-emerald-400/10";
  if (t.startsWith("Mirror —") || t.startsWith("Schema validated"))
    return "text-sky-300 border-sky-400/30 bg-sky-500/[0.06]";
  if (t.startsWith("Canary architecture prepared"))
    return "text-sky-300 border-sky-400/30 bg-sky-500/[0.06]";
  if (t.startsWith("Prepared —"))
    return "text-zinc-300 border-zinc-500/30 bg-zinc-500/10";
  if (t.startsWith("Collection stale"))
    return "text-rose-300 border-rose-400/40 bg-rose-500/10";
  if (t.startsWith("Live status not verified") || t.startsWith("Pending"))
    return "text-amber-300 border-amber-400/30 bg-amber-500/[0.05]";
  return STATUS_CLS[t] || "text-sky-300 border-sky-400/25 bg-sky-500/[0.05]";
};

export default function SystemHealth() {
  const [h, setH] = useState(null);
  const [contam, setContam] = useState(null);
  useEffect(() => {
    api.get("/audit/system-health").then((r) => setH(r.data)).catch(() => setH(null));
    api.get("/audit/contamination").then((r) => setContam(r.data)).catch(() => setContam(null));
  }, []);
  if (!h) return <div className="p-8 text-[#71717A] font-mono text-sm">Loading system health…</div>;
  const Pill = ({ s }) => <span className={`text-[10px] font-mono px-2 py-0.5 border ${cls(s)}`}>{s}</span>;
  const Section = ({ title, rows, keyName }) => (
    <div className="border border-[#27272A] bg-[#101013] p-3" data-testid={`health-${keyName}`}>
      <div className="text-[10px] uppercase tracking-widest text-[#71717A] font-mono mb-2">{title}</div>
      {rows.map((r, i) => (
        <div key={i} className="flex flex-wrap items-center justify-between gap-2 py-1.5 border-b border-[#1c1c1f] last:border-0">
          <div><div className="text-sm text-[#E4E4E7]">{r.source || r.layer}</div>
            {r.role && <div className="text-[10px] text-[#52525B] font-mono">{r.role}</div>}
            {r.concepts_tracked != null && <div className="text-[10px] text-[#A1A1AA] font-mono">{r.concepts_tracked} concepts tracked · {r.concepts_with_enough_history} with enough history · {r.raw_asin_observations} observations{r.run_origin ? ` · collector runs: ${r.run_origin === "live" ? "100% live-origin" : r.run_origin}` : ""}{r.latest_observation_at ? ` · latest ${String(r.latest_observation_at).slice(0,10)}` : ""}</div>}
            {r.note && <div className="text-[10px] text-amber-300/70 font-mono">{r.note}</div>}</div>
          <Pill s={r.status} />
        </div>
      ))}
    </div>
  );
  return (
    <div className="p-8 max-w-[1000px]" data-testid="system-health-page">
      <div className="flex items-center gap-2"><ShieldCheck size={20} className="text-emerald-400" />
        <h1 className="font-head text-3xl font-bold">System Health</h1></div>
      <div className="text-[11px] font-mono text-[#52525B] mt-1">{h.note}</div>

      {contam && (
        <div className={`border p-3 mt-4 ${contam.clean ? "border-emerald-400/30 bg-emerald-400/[0.05]" : "border-rose-400/40 bg-rose-500/10"}`} data-testid="health-contamination">
          <div className="text-sm">{contam.clean ? "✓ Mock / real isolation clean — no offline data has leaked into real signals." : `⚠ ${contam.findings.length} contamination finding(s)`}</div>
          {!contam.clean && contam.findings.map((f, i) => <div key={i} className="text-xs text-rose-300 mt-1">[{f.severity}] {f.collection}: {f.issue}</div>)}
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 mt-4">
        <Section title="Core discovery sources" rows={h.discovery} keyName="discovery" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Section title="Independent demand confirmation" rows={h.confirmation} keyName="confirmation" />
          <Section title="Early innovation" rows={h.early_innovation} keyName="early" />
        </div>
        <Section title="Research & decision layers" rows={h.research_layers} keyName="research" />
      </div>

      <div className="border border-[#27272A] bg-[#0c0c0e] p-3 mt-4" data-testid="health-philosophy">
        <div className="text-[10px] uppercase tracking-widest text-[#71717A] font-mono mb-2">Decision philosophy</div>
        <div className="flex flex-wrap items-center gap-2 text-xs text-[#A1A1AA]">
          {h.decision_philosophy.map((p, i) => (
            <span key={i} className="flex items-center gap-2">{i > 0 && <span className="text-[#52525B]">→</span>}{p}</span>
          ))}
        </div>
      </div>
    </div>
  );
}
